import React, { useContext } from 'react'
import { FormLayoutRow, Well } from '@pushly/aqe/lib/components'
import { Form, Radio, Space, Typography } from 'antd'
import { PromptVisitorConditions } from '@pushly/models/lib/structs/prompts/prompt-visitor-conditions'
import { VisitorCondition } from '../types'
import { TimeOnPageConditionEditor } from './visitor-conditions/time-on-page-condition-editor'
import { SessionPageViewsConditionEditor } from './visitor-conditions/session-page-views-condition-editor'
import { SessionTimeOnSiteCondition } from './visitor-conditions/session-time-on-site-condition-editor'
import { ScrollDepthConditionEditor } from './visitor-conditions/scroll-depth-condition-editor'
import {
    DEFAULT_SCROLL_DEPTH,
    DEFAULT_SESSION_PAGE_VIEWS,
    DEFAULT_SESSION_TIME_ON_SITE_SECONDS,
    DEFAULT_TIME_ON_PAGE_SECONDS,
} from '../constants'
import { PromptEditorDispatchContext } from '../reducer'
import { PromptEditorContext } from '../context'

export const PromptVisitorConditionsEditor = () => {
    const dispatch = useContext(PromptEditorDispatchContext)
    const { loading, selectedPrompt, selectedPromptIdx } = useContext(PromptEditorContext)

    const visitorConditions = selectedPrompt?.conditions?.visitor

    let selectedConditionType: VisitorCondition = VisitorCondition.EVERYONE
    if (visitorConditions) {
        if ('timeOnPageSeconds' in visitorConditions) {
            selectedConditionType = VisitorCondition.TIME_ON_PAGE
        } else if ('sessionPageViews' in visitorConditions) {
            selectedConditionType = VisitorCondition.SESSION_PAGE_VIEWS
        } else if ('scrollDepthPct' in visitorConditions) {
            selectedConditionType = VisitorCondition.SCROLL_DEPTH
        } else if ('sessionTimeOnSiteSeconds' in visitorConditions) {
            selectedConditionType = VisitorCondition.SESSION_TIME_ON_SITE
        }
    }

    function setVisitorConditions(conditionsUpdate: PromptVisitorConditions | undefined): void {
        if (loading) return

        const update = selectedPrompt.clone()
        update.conditions ??= new PromptVisitorConditions()
        update.conditions.visitor = conditionsUpdate

        dispatch({ type: 'set-prompt', idx: selectedPromptIdx, prompt: update })
    }

    return (
        <Well className="visitor-conditions-well" title="Visitor Conditions" hideFooter={true} mode="ghost" flush>
            <Space vertical>
                <Typography.Paragraph>
                    By default this prompt will be shown to the visitor immediately. You may choose to delay the prompt
                    display by enabling one of the below conditions.
                </Typography.Paragraph>

                <Form.Item layout="vertical" label="Users will receive this prompt after">
                    <Radio.Group
                        key={selectedPromptIdx}
                        size="small"
                        buttonStyle="solid"
                        defaultValue={selectedConditionType}
                        onChange={(ev) => {
                            const nextConditionType = ev.target.value as VisitorCondition
                            let nextCondition: PromptVisitorConditions | undefined

                            switch (nextConditionType) {
                                case VisitorCondition.TIME_ON_PAGE: {
                                    nextCondition = PromptVisitorConditions.from({
                                        timeOnPageSeconds: DEFAULT_TIME_ON_PAGE_SECONDS,
                                    })
                                    break
                                }
                                case VisitorCondition.SESSION_PAGE_VIEWS: {
                                    nextCondition = PromptVisitorConditions.from({
                                        sessionPageViews: DEFAULT_SESSION_PAGE_VIEWS,
                                    })
                                    break
                                }
                                case VisitorCondition.SCROLL_DEPTH: {
                                    nextCondition = PromptVisitorConditions.from({
                                        scrollDepthPct: DEFAULT_SCROLL_DEPTH,
                                    })
                                    break
                                }
                                case VisitorCondition.SESSION_TIME_ON_SITE: {
                                    nextCondition = PromptVisitorConditions.from({
                                        sessionTimeOnSiteSeconds: DEFAULT_SESSION_TIME_ON_SITE_SECONDS,
                                    })
                                    break
                                }
                            }

                            setVisitorConditions(nextCondition)
                        }}
                        options={[
                            { value: VisitorCondition.EVERYONE, label: 'Page Load' },
                            { value: VisitorCondition.TIME_ON_PAGE, label: 'Time on Page' },
                            { value: VisitorCondition.SESSION_PAGE_VIEWS, label: '# of Page Views' },
                            { value: VisitorCondition.SCROLL_DEPTH, label: 'Scroll Depth' },
                            { value: VisitorCondition.SESSION_TIME_ON_SITE, label: 'Time on Site' },
                        ]}
                        optionType="button"
                    />
                </Form.Item>

                {selectedConditionType === VisitorCondition.TIME_ON_PAGE && (
                    <TimeOnPageConditionEditor value={visitorConditions} onChange={setVisitorConditions} />
                )}
                {selectedConditionType === VisitorCondition.SESSION_PAGE_VIEWS && (
                    <SessionPageViewsConditionEditor value={visitorConditions} onChange={setVisitorConditions} />
                )}
                {selectedConditionType === VisitorCondition.SCROLL_DEPTH && (
                    <ScrollDepthConditionEditor value={visitorConditions} onChange={setVisitorConditions} />
                )}
                {selectedConditionType === VisitorCondition.SESSION_TIME_ON_SITE && (
                    <SessionTimeOnSiteCondition value={visitorConditions} onChange={setVisitorConditions} />
                )}
            </Space>
        </Well>
    )
}
